import { useXScale, useDrawingArea } from '@mui/x-charts-pro';
import { Box } from '@mui/material';
import { Schedule } from '@mui/icons-material';
import { formatMsUTCToHM } from '@/utilities/timeUtils';
import { TachographDriversName } from './TachographService';
import { getMarkerData } from '@/components/map/markers/TargetMarkerChooser';
import { TextBubbleMarker } from './TextBubbleMarker';

const SHOW_DRIVER_NAME_WIDTH_PX = 120;
const DRIVER_ICON_SIZE_PX = 14;

const DRIVER_SVG_HREF_BLACK = getIconAsSvgDataURI('#000000');
const DRIVER_SVG_HREF_WHITE = getIconAsSvgDataURI('#ffffff');

function getIconAsSvgDataURI(color: string) {
  const driverIconSvgContent = getMarkerData('TACHO_DRIVE', DRIVER_ICON_SIZE_PX, DRIVER_ICON_SIZE_PX, color);
  return `data:image/svg+xml;utf8,${encodeURIComponent(driverIconSvgContent)}`;
}

export function DriversNameLayer({ driversName }: { driversName: TachographDriversName[] }) {
  const { top, height } = useDrawingArea();
  const xScale = useXScale();

  const sortedDrivers = [...driversName].sort((a, b) => a.timeInDayMillis - b.timeInDayMillis);

  function isTooClose(index: number, currentPX: number): boolean {
    if (index < 0 || index >= sortedDrivers.length) {
      return false;
    }
    const positionPX = xScale?.(sortedDrivers[index].timeInDayMillis / 3_600_000);
    if (!positionPX) {
      return false;
    }
    return Math.abs(positionPX - currentPX) < SHOW_DRIVER_NAME_WIDTH_PX / 2;
  }

  return (
    <g pointerEvents="none" data-qa-element-id="drivers_name_layer">
      {sortedDrivers.map((driverNameInfo, index) => {
        const currentTime = driverNameInfo.timeInDayMillis;
        const positionPx = xScale?.(currentTime / 3_600_000);
        if (!positionPx) {
          return null;
        }

        const showOnlyIcon = isTooClose(index - 1, positionPx) || isTooClose(index + 1, positionPx);
        const driverName = driverNameInfo.driverName;

        const pillWidth = showOnlyIcon ? 18 : SHOW_DRIVER_NAME_WIDTH_PX;

        const tooltipTitle = (
          <Box style={{ display: 'flex', alignItems: 'center', fontSize: '10px !important' }}>
            <strong data-qa-element-id="tooltip_date">
              <Schedule sx={{ fontSize: '12px', marginBottom: '-2px', marginRight: '5px' }} />[
              {formatMsUTCToHM(driverNameInfo.timeInDayMillis)}]
            </strong>
            <img
              src={DRIVER_SVG_HREF_WHITE}
              width={DRIVER_ICON_SIZE_PX}
              height={DRIVER_ICON_SIZE_PX}
              style={{ display: 'inline-block', marginTop: '2px', marginLeft: '3px', marginRight: '3px' }}
            />
            <span data-qa-element-id="driver_name">{driverName}</span>
          </Box>
        );

        const contentTitle = (
          <Box
            style={{
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              lineClamp: 1,
              WebkitLineClamp: 1,
            }}
          >
            <img
              src={DRIVER_SVG_HREF_BLACK}
              className="user-select-disabled"
              style={{
                display: 'block',
                float: 'left',
              }}
              width={DRIVER_ICON_SIZE_PX}
              height={DRIVER_ICON_SIZE_PX}
            />
            {!showOnlyIcon && <span style={{ marginLeft: '3px' }}>{driverName}</span>}
          </Box>
        );

        return (
          <TextBubbleMarker
            key={`DriverName-BubbleMarker-${index}`}
            index={index}
            positionPx={positionPx}
            pillWidth={pillWidth}
            top={top - 42}
            bottom={top + height}
            contentTitle={contentTitle}
            tooltipTitle={tooltipTitle}
          />
        );
      })}
    </g>
  );
}
